package services.storage.http

import it.neckar.lizergy.model.company.CompanyResolver
import it.neckar.lizergy.model.company.UserResolver
import it.neckar.lizergy.model.configuration.quote.QuoteConfigurations
import it.neckar.lizergy.model.price.AvailableProducts
import it.neckar.lizergy.model.price.PriceList
import it.neckar.lizergy.model.price.ProductResolver
import it.neckar.lizergy.model.project.ResolvedProject
import it.neckar.lizergy.model.project.Verification
import it.neckar.lizergy.model.project.getBelongsToCompanyCodes
import it.neckar.lizergy.model.project.process.state.DocumentationProcessStateEntry
import it.neckar.lizergy.model.stumps.AdvanceInvoice
import it.neckar.lizergy.model.stumps.AssemblyBasement
import it.neckar.lizergy.model.stumps.AssemblyRoof
import it.neckar.lizergy.model.stumps.Documentation
import it.neckar.lizergy.model.stumps.FinalAccount
import it.neckar.lizergy.model.stumps.FinishingUp
import it.neckar.lizergy.model.stumps.GridAssessment
import it.neckar.lizergy.model.stumps.OrderSpecialMaterial
import it.neckar.lizergy.model.stumps.StartupOperations
import it.neckar.lizergy.model.stumps.SwitchMeterBox
import kotlinx.serialization.Serializable
import serialized.AccountingSerializedProjectPreview
import serialized.SerializedAssemblyPortfolio
import serialized.SerializedBlueprint
import serialized.SerializedProject
import serialized.SerializedProjectPreview
import serialized.toBlueprintPreview

/**
 * This class is serialized in the PDFs
 */
@Serializable
data class FetchProjectInformation(
  val photovoltaicsProject: SerializedProject,
  val blueprint: SerializedBlueprint,
  val configurations: List<FetchConfiguration>,
  val orderSpecialMaterial: OrderSpecialMaterial,
  val gridAssessment: GridAssessment,
  val assemblyPortfolio: SerializedAssemblyPortfolio,
  val advanceInvoice: AdvanceInvoice,
  val assemblyRoof: AssemblyRoof,
  val assemblyBasement: AssemblyBasement,
  val switchMeterBox: SwitchMeterBox,
  val startupOperations: StartupOperations,
  val finishingUp: FinishingUp,
  val finalAccount: FinalAccount,
  val documentation: Documentation,
  val documentationProcessStateEntry: DocumentationProcessStateEntry?,
  val verification: Verification?,
) {

  fun toResolvedProject(availableProducts: AvailableProducts, productResolver: ProductResolver, priceList: PriceList, userResolver: UserResolver, companyResolver: CompanyResolver): ResolvedProject {
    return photovoltaicsProject.resolve(
      blueprint = blueprint.resolve(
        productResolver = productResolver,
        userResolver = userResolver,
        companyResolver = companyResolver,
      ),
      quoteConfigurations = QuoteConfigurations(configurations.map {
        it.toQuoteConfiguration(
          availableProducts = availableProducts,
          productResolver = productResolver,
          priceList = priceList,
          userResolver = userResolver,
          companyResolver = companyResolver,
        )
      }),
      orderSpecialMaterial = orderSpecialMaterial,
      gridAssessment = gridAssessment,
      assemblyPortfolio = assemblyPortfolio.resolve(userResolver, companyResolver),
      advanceInvoice = advanceInvoice,
      assemblyRoof = assemblyRoof,
      assemblyBasement = assemblyBasement,
      switchMeterBox = switchMeterBox,
      startupOperations = startupOperations,
      finishingUp = finishingUp,
      finalAccount = finalAccount,
      documentation = documentation,
      verification = verification,
      userResolver = userResolver,
      companyResolver = companyResolver,
    )
  }

  fun toProjectPreview(): SerializedProjectPreview {
    return SerializedProjectPreview(
      projectId = photovoltaicsProject.projectId,
      projectName = photovoltaicsProject.projectName,
      sellingCompany = photovoltaicsProject.sellingCompany,
      customer = photovoltaicsProject.customer,
      maintainer = photovoltaicsProject.maintainer,
      ansprechpartnerDach = assemblyPortfolio.ansprechpartnerDach,
      ansprechpartnerElektro = assemblyPortfolio.ansprechpartnerElektro,
      acquisitionDate = blueprint.acquisitionDate,
      blueprintPreview = blueprint.toBlueprintPreview(),
      configurationPreviews = configurations.map { configuration -> configuration.toConfigurationPreview() },
      orderSpecialMaterialId = orderSpecialMaterial.uuid,
      gridAssessmentId = gridAssessment.uuid,
      assemblyPortfolioId = assemblyPortfolio.uuid,
      advanceInvoiceId = advanceInvoice.uuid,
      assemblyRoofId = assemblyRoof.uuid,
      assemblyBasementId = assemblyBasement.uuid,
      switchMeterBoxId = switchMeterBox.uuid,
      startupOperationsId = startupOperations.uuid,
      finishingUpId = finishingUp.uuid,
      finalAccountId = finalAccount.uuid,
      documentationId = documentation.uuid,
      startupDate = startupOperations.date,
      verification = verification,
      restArbeiten = assemblyBasement.restArbeiten,
      belongsToCompanies = photovoltaicsProject.getBelongsToCompanyCodes(blueprint, configurations.map { it.configuration }),
      archiveReasons = photovoltaicsProject.archiveReasons,
    )
  }

  fun toAccountingProjectPreview(
    availableProducts: AvailableProducts,
    productResolver: ProductResolver,
    priceList: PriceList,
  ): AccountingSerializedProjectPreview {
    return AccountingSerializedProjectPreview(
      projectId = photovoltaicsProject.projectId,
      projectName = photovoltaicsProject.projectName,
      sellingCompany = photovoltaicsProject.sellingCompany,
      customer = photovoltaicsProject.customer,
      maintainer = photovoltaicsProject.maintainer,
      ansprechpartnerDach = assemblyPortfolio.ansprechpartnerDach,
      ansprechpartnerElektro = assemblyPortfolio.ansprechpartnerElektro,
      acquisitionDate = blueprint.acquisitionDate,
      blueprintPreview = blueprint.toBlueprintPreview(),
      configurationPreviews = configurations.map { configuration ->
        configuration.toAccountingConfigurationPreview(
          availableProducts = availableProducts,
          productResolver = productResolver,
          priceList = priceList,
        )
      },
      orderSpecialMaterialId = orderSpecialMaterial.uuid,
      gridAssessmentId = gridAssessment.uuid,
      assemblyPortfolioId = assemblyPortfolio.uuid,
      advanceInvoiceId = advanceInvoice.uuid,
      assemblyRoofId = assemblyRoof.uuid,
      assemblyBasementId = assemblyBasement.uuid,
      switchMeterBoxId = switchMeterBox.uuid,
      startupOperationsId = startupOperations.uuid,
      finishingUpId = finishingUp.uuid,
      finalAccountId = finalAccount.uuid,
      documentationId = documentation.uuid,
      startupDate = startupOperations.date,
      verification = verification,
      restArbeiten = assemblyBasement.restArbeiten,
      belongsToCompanies = photovoltaicsProject.getBelongsToCompanyCodes(blueprint, configurations.map { it.configuration }),
      archiveReasons = photovoltaicsProject.archiveReasons,
    )
  }

}
